import React, { useEffect, useState } from 'react'
import TeamOdd from './TeamOdd.tsx';
import { useBetContext } from '../../../../context/BetContext.tsx';
import { useUser } from '../../../../context/UserContext.tsx';
import { BetDetails, PlaceWagersResult } from '../../../../models/MyBetModel.tsx';
import { useNavigate } from "react-router-dom";
import { RiArrowDownWideFill } from "react-icons/ri";
import { IoMdCloseCircle } from "react-icons/io";
import OddFuture from '../future/OddFuture.tsx';
import { applyNewOddToBet, calcProfit, calcStake, formatCurrency, formatNumber, formatNumbers, isLiveBet, matchesBetForOddChange, shouldShowDelayedBetMessage } from '../../../../helpers/functions.tsx';
import { placeApiAllWagers } from '../../../../services/player/apis/Schelude.tsx';
import { logUserActivity } from '../../../../services/player/LoginService.tsx';
import { useModal } from '../../../../context/ModalContext.tsx';
import { BetSlipActions, BetSlipChrome, BetSlipMode, BetSlipStake, BetSlipStakeFocus } from '../BetSlipChrome.tsx';
import { useBetContext as useParlayContext } from '../../../../context/ParlayBetContext.tsx';
import { useSport } from '../../../../context/SportContext.tsx';
import { convertStraightsToParlay } from '../../../../helpers/betSlipModeSwitch.ts';
import { loadBetSlipContent } from '../../../../helpers/openBetSlipMode.tsx';
import WagerConfirmedSlip from '../WagerConfirmedSlip.tsx';
interface StraightBetsResponsiveProps {
    setShowLimitAlert?: (value: boolean) => void;
    setErrorMessage?: (value: string) => void;
}

function isFutureDetails(bet: any): boolean {
    return bet?.betType === "Contest";
}

const StraightBets = (props: StraightBetsResponsiveProps) => {
    const { setShowLimitAlert, setErrorMessage } = props;
    const { user, updateUser, userIpData } = useUser();
    const { clearBets, bets, setBets, updateBet, useFreePlay, setUseFreePlay } = useBetContext();
    const total = bets.reduce((acc, bet) => acc + Number(bet.bet), 0);
    const totalReward: number = bets.reduce((accumulator, currentValue) => accumulator + (currentValue.reward ?? 0), 0);
    const totalBets = bets.length;
    const [userBet, setUserBet] = useState<(BetDetails)[]>([]);
    const [showPendingOnly, setShowPendingOnly] = useState(false);
    const [ticketNumber, setTicketNumber] = useState<number | null>(null);
    const [oddsImproved, setOddsImproved] = useState<boolean | null>(null);
    const [isPlacing, setIsPlacing] = useState(false);
    const bet = userBet.reduce((accumulator, currentValue) => accumulator + Number(currentValue.bet), 0);
    const reward = userBet.reduce((accumulator, currentValue) => accumulator + (currentValue.reward ?? 0), 0);
    const [changedBets, setChangedBets] = useState<any[]>([]);
    const [showOddsChangedModal, setShowOddsChangedModal] = useState(false);
    const [autoAcceptOdds, setAutoAcceptOdds] = useState(false);
    const [stakeFocus, setStakeFocus] = useState<BetSlipStakeFocus>('risk');
    const [parlaySwitchNotice, setParlaySwitchNotice] = useState<string | null>(null);
    const { closeModal, updateModalContent } = useModal();
    const { setBets: setParlayBets } = useParlayContext();
    const { setIsParlay } = useSport();
    const navigate = useNavigate();
    const shouldShowDelayedMessage = bets.some(bet => shouldShowDelayedBetMessage(bet));
    const hasLiveBets = bets.some(bet => isLiveBet(bet));

    useEffect(() => {
        if (bets.length > 0 && !showPendingOnly) {
            setShowPendingOnly(false);
            setUserBet([]);
            setTicketNumber(null);
            setOddsImproved(null);
        }
    }, [bets, showPendingOnly]);

    const applyChangedOddsToBets = (currentBets: BetDetails[], changedList: any[]) =>
        currentBets.map(bet => {
            const changed = changedList.find(cb => matchesBetForOddChange(cb, bet));
            if (!changed?.newOdd) return bet;
            return applyNewOddToBet(bet, changed.newOdd);
        });

    const confirmedBet = async () => {
        setIsPlacing(true);

        try {
            if (!user || !user.id || !user.parent) return;

            const userId = user.id;
            let currentBets = bets;
            const maxAttempts = autoAcceptOdds ? 3 : 1;

            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                const sanitizedBets = currentBets.map(({ odds, newOdd, ...bet }: any) => bet);
                const result: PlaceWagersResult = await placeApiAllWagers(userId, sanitizedBets, useFreePlay);

                if (result?.errors?.[0]?.error?.errorType === "oddChange") {
                    const changedList = result.changedBets ?? [];
                    if (autoAcceptOdds && attempt < maxAttempts - 1 && changedList.some(cb => cb?.newOdd)) {
                        currentBets = applyChangedOddsToBets(currentBets, changedList);
                        setBets(currentBets);
                        setChangedBets([]);
                        setShowOddsChangedModal(false);
                        continue;
                    }

                    setChangedBets(changedList);
                    setShowOddsChangedModal(true);
                    return;
                }

                if (result?.hasErrors) {
                    const firstError = result?.errors?.[0]?.error;
                    const message = firstError?.message
                        ? `${firstError.message}`
                        : 'There was an error placing one or more bets.';

                    setShowLimitAlert?.(true);
                    setErrorMessage?.(message);
                    return;
                }

                const placedBets = (result?.bets?.length ? result.bets : sanitizedBets) as BetDetails[];
                const totalStake = placedBets.reduce((sum, b) => sum + Number(b.bet ?? 0), 0);
                const ticketFromResult = placedBets.find(b => b.ticket)?.ticket ?? null;

                setUserBet(placedBets);
                setTicketNumber(
                    ticketFromResult
                        ? Number(String(ticketFromResult).split('-')[0]) || null
                        : null
                );

                if (useFreePlay) {
                    updateUser({
                        freePlay: parseFloat(((user.freePlay ?? 0) - totalStake).toFixed(2)),
                    });
                } else {
                    updateUser({
                        available: parseFloat(((user.available ?? 0) - totalStake).toFixed(2)),
                    });
                }

                for (const placedBet of placedBets) {
                    const risk = Number(placedBet.bet ?? 0).toFixed(2);
                    const win = Number(placedBet.reward ?? 0).toFixed(2);
                    const ticket = placedBet.ticket ?? ticketFromResult ?? '';
                    logUserActivity(
                        user.userName,
                        `Ticket: ${ticket} Risk: $${risk} Win: $${win}`,
                        userIpData
                    );
                }

                setShowPendingOnly(true);
                clearBets();
                setUseFreePlay?.(false);
                return;
            }

        } catch (error) {
            console.error(error);
            setErrorMessage?.("Error placing bets.");
        } finally {
            setIsPlacing(false);
        }
    };

    const resetBetState = () => {
        setShowPendingOnly(false);
        setTicketNumber(null);
        setUserBet([])
        setOddsImproved(null);
        setUseFreePlay?.(false);
        closeModal();

    };

    const closeModalTotal = () => {
        closeModal();
        setShowPendingOnly(false);
    }

    const handleClearAll = () => {
        clearBets();
        setUseFreePlay(false);
        setChangedBets([]);
        closeModal();
    };

    useEffect(() => {
        if (showPendingOnly || isPlacing) return;
        if (bets.length === 0) closeModal();
    }, [bets.length, showPendingOnly, isPlacing, closeModal]);

    useEffect(() => {
        if (showPendingOnly || isPlacing) return;
        if (bets.length !== 1) return;
        if (window.innerWidth >= 1300) return;

        let cancelled = false;
        void import('./strainghtResponsive/OneBet.tsx').then((confirmSlip) => {
            if (cancelled) return;
            const ConfirmSlip = confirmSlip.default;
            updateModalContent(
                <ConfirmSlip
                    setShowLimitAlert={setShowLimitAlert}
                    setErrorMessage={setErrorMessage}
                />
            );
        });

        return () => {
            cancelled = true;
        };
    }, [bets.length, showPendingOnly, isPlacing, setShowLimitAlert, setErrorMessage, updateModalContent]);

    const acceptNewOdds = () => {
        const updated = applyChangedOddsToBets(bets, changedBets);
        setBets(updated);
        setChangedBets([]);
        setShowOddsChangedModal(false);
        setErrorMessage?.('');
    };

    const singleBet = bets.length === 1 ? bets[0] : null;

    const handleSingleRiskChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!singleBet) return;
        const value = event.target.value;
        const price = singleBet.odd?.price ?? 0;
        if (value === '') {
            updateBet({ ...singleBet, bet: 0, reward: 0, odd: singleBet.odd });
            return;
        }
        const stake = Math.round(parseFloat(value));
        if (isNaN(stake) || stake < 0) return;
        updateBet({ ...singleBet, bet: stake, reward: calcProfit(stake, price), odd: singleBet.odd });
    };

    const handleSingleWinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!singleBet) return;
        const value = event.target.value;
        const price = singleBet.odd?.price ?? 0;
        if (value === '') {
            updateBet({ ...singleBet, bet: 0, reward: 0, odd: singleBet.odd });
            return;
        }
        const win = Math.round(parseFloat(value));
        if (isNaN(win) || win < 0) return;
        updateBet({ ...singleBet, bet: calcStake(win, price), reward: win, odd: singleBet.odd });
    };

    useEffect(() => {
        setParlaySwitchNotice(null);
    }, [bets]);

    const handleTabChange = async (nextMode: BetSlipMode) => {
        if (nextMode !== 'parlay') return;

        const converted = convertStraightsToParlay(bets);
        if (!converted.ok) {
            setParlaySwitchNotice(converted.message);
            return;
        }

        setParlaySwitchNotice(null);
        setParlayBets(converted.selections);
        setIsParlay(true);
        navigate('/home/parlay');
        const content = await loadBetSlipContent('parlay', { setShowLimitAlert, setErrorMessage });
        updateModalContent(content);
    };

    if (!user) return null;

    if (showPendingOnly) {
        return (
            <div className="bet-slip bet-slip--straight">
                <WagerConfirmedSlip
                    legs={userBet}
                    risk={bet}
                    win={reward}
                    ticketNumber={ticketNumber}
                    onClose={resetBetState}
                    showLegAmounts
                />
            </div>
        );
    }

    return (
        <div className={`bet-slip bet-slip--straight${bets.length === 1 ? ' bet-slip--single' : ''}`}>
            <div className="modal-title-acount bet-slip-mobile-chrome">
                <h1 className="modal-title fs-5 teko place">PLACE YOUR BETS</h1>
                <IoMdCloseCircle className='close-modal' onClick={closeModalTotal} />
                <RiArrowDownWideFill className='icon-close-modal' onClick={closeModalTotal} />
            </div>
            <BetSlipChrome
                pickCount={showPendingOnly ? userBet.length : totalBets}
                mode="straight"
                onClose={closeModalTotal}
                onTabChange={handleTabChange}
                notice={parlaySwitchNotice}
                hideTabs
            />
            {!showPendingOnly && (
                <>
                    <div className="bet-slip__scroll">
                    <div className='titlegame'>
                        <span className="text-title-bet no-display-small">PLEASE CONFIRM YOUR BET</span>
                        <span className="text-title-bet no-display">Review and confirm</span>
                        <span className="text-title-bet no-display">{totalBets} Wager(s)</span>
                    </div>
                    <div className='gamecontainer height-tex slip-picks slip-picks--straight'>
                        {bets.map((bet, i) =>
                            isFutureDetails(bet) ? (
                                <OddFuture key={i} bet={bet} />
                            ) : (
                                <TeamOdd key={i} bet={bet} oddsImproved={oddsImproved} />
                            )
                        )}
                    </div>

                        {showOddsChangedModal && (
                            <div className="odds-changed-alert">
                                <div className="odds-changed-card">
                                    <div className="odds-changed-header">
                                        <span className="odds-changed-icon">!</span>
                                        <div className="odds-changed-titles">
                                            <h4 className="odds-changed-title">Odds have changed</h4>
                                            <p className="odds-changed-subtitle">
                                                Odds have changed. Please review the changes and accept.
                                            </p>
                                        </div>
                                    </div>

                                    <ul className="odds-changed-list">
                                        {changedBets.map((bet, index) => {
                                            const oldPrice = bet.odd.price ?? 0;
                                            const newPrice = bet.newOdd.price ?? 0;
                                            const improved = newPrice > oldPrice;
                                            const isMoneyline =
                                                bet.marketName === 'Moneyline' ||
                                                bet.odd?.market_id === 'moneyline' ||
                                                bet.newOdd?.market_id === 'moneyline';
                                            const showOldPoints = !isMoneyline && bet.odd?.points != null;
                                            const showNewPoints = !isMoneyline && bet.newOdd?.points != null;
                                            return (
                                                <li key={index} className="odds-changed-item">
                                                    <span className="odds-changed-bet-name">
                                                        {bet.betName}
                                                    </span>
                                                    <div className="odds-changed-row">
                                                        <span className="odds-old">
                                                            {showOldPoints ? `${formatNumber(bet.odd.points, bet.odd.selection_line, "points")} ` : ''}({formatNumber(bet.odd.price, bet.odd.selection_line)})
                                                        </span>
                                                        <span className="odds-arrow">→</span>
                                                        <span className={`odds-new ${improved ? 'odds-up' : 'odds-down'}`}>
                                                            {showNewPoints ? `${formatNumber(bet.newOdd.points, bet.newOdd.selection_line, "points")} ` : ''}({formatNumber(bet.newOdd.price, bet.newOdd.selection_line)})
                                                        </span>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>

                                    <div className="odds-changed-actions">
                                        <button
                                            type="button"
                                            className="odds-changed-btn-cancel"
                                            onClick={() => { setChangedBets([]); setShowOddsChangedModal(false); }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="odds-changed-btn-accept"
                                            onClick={acceptNewOdds}
                                        >
                                            Accept New Odds
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='container-total'>
                        <div className='total-content'>
                            <div className='bet-content'>
                                <span className='bet-text'>BET TOTAL:</span>
                                <span className='price'>
                                    ${Math.round(total).toLocaleString("en-US")}
                                </span>
                            </div>
                            <div className='bet-content'>
                                <span className='bet-text'>TOTAL OF POSSIBLE PRIZE:</span>
                                ${Math.round(totalReward).toLocaleString("en-US")}
                            </div>
                        </div>


                        {(user?.freePlay ?? 0) > 0 && (
                            <div className="container-input-amount">
                                <input type="checkbox" className="checkbox-pitcher" checked={useFreePlay} onChange={() => setUseFreePlay(prev => !prev)} />
                                <span className="user-free-play">
                                    Use your freeplay balance of ${formatNumbers(user?.freePlay)}
                                </span>
                            </div>
                        )}
                        {hasLiveBets && (
                            <label className="auto-accept-odds-row">
                                <input
                                    type="checkbox"
                                    className="checkbox-pitcher"
                                    checked={autoAcceptOdds}
                                    onChange={() => setAutoAcceptOdds(prev => !prev)}
                                />
                                <span className="auto-accept-odds-label">
                                    Automatically accept updated odds
                                </span>
                            </label>
                        )}
                        {shouldShowDelayedMessage && (
                            <div className="delayed-message-marekts">
                                <span className='font-message-delayed'>*This bet may take a little longer to grade.</span>
                                <span className='font-message-delayed'>Thank you for understanding!</span>
                            </div>
                        )}


                        <div className='button-content'>
                            <button className='clean' data-bs-dismiss="modal" onClick={handleClearAll}>CLEAR ALL</button>
                            <button className='confirm-bet' onClick={confirmedBet} disabled={isPlacing || showOddsChangedModal}>
                                {isPlacing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Please wait a moment...
                                    </>
                                ) : (
                                    "Place Wager"
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="bet-slip__footer">
                        <BetSlipStake
                            focus={stakeFocus}
                            onFocusChange={setStakeFocus}
                            risk={singleBet ? (singleBet.bet || '') : Math.round(total)}
                            toWin={singleBet ? (singleBet.reward || '') : Math.round(totalReward)}
                            onRiskChange={handleSingleRiskChange}
                            onWinChange={handleSingleWinChange}
                            showInputs={!!singleBet}
                            hideToggle={!singleBet}
                            splitTotals={!singleBet}
                            riskTotalLabel={singleBet ? undefined : 'Total Risk'}
                            winTotalLabel={singleBet ? undefined : 'Total To Win'}
                            betTotal={formatCurrency(total)}
                            possibleWin={formatCurrency(totalReward)}
                        />
                        {(user?.freePlay ?? 0) > 0 && (
                            <div className="container-input-amount">
                                <input type="checkbox" className="checkbox-pitcher" checked={useFreePlay} onChange={() => setUseFreePlay(prev => !prev)} />
                                <span className="user-free-play">
                                    Use your freeplay balance of ${formatNumbers(user?.freePlay)}
                                </span>
                            </div>
                        )}
                        {hasLiveBets && (
                            <label className="auto-accept-odds-row">
                                <input
                                    type="checkbox"
                                    className="checkbox-pitcher"
                                    checked={autoAcceptOdds}
                                    onChange={() => setAutoAcceptOdds(prev => !prev)}
                                />
                                <span className="auto-accept-odds-label">
                                    Automatically accept updated odds
                                </span>
                            </label>
                        )}
                        {shouldShowDelayedMessage && (
                            <div className="delayed-message-marekts">
                                <span className='font-message-delayed'>*This bet may take a little longer to grade.</span>
                                <span className='font-message-delayed'>Thank you for understanding!</span>
                            </div>
                        )}
                        <BetSlipActions
                            mode="straight"
                            onClear={handleClearAll}
                            onPlace={confirmedBet}
                            placeDisabled={showOddsChangedModal}
                            placeBusy={isPlacing}
                            placeLabel={singleBet ? undefined : 'Place Bets'}
                            showPlaceIcon={!!singleBet}
                        />
                    </div>


                </>
            )}

        </div>
    );


};

export default StraightBets